import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "@/icons";
import { cn } from "@/lib/utils";
import { Container } from "@/components/common/Container";
import { Logo } from "@/components/common/Logo";
import { CtaButton } from "@/components/common/CtaButton";
import { AnnouncementBar } from "@/components/home/AnnouncementBar";
import { mainNav } from "@/data/navigation";
import { siteConfig } from "@/config/site.config";
import { EASE } from "@/animations/motion";

/** Global site header — announcement bar + sticky adaptive nav. */
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50">
      <AnnouncementBar />
      <div
        className={cn(
          "transition-all duration-300",
          scrolled
            ? "border-b border-border/70 bg-background/80 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent",
        )}
      >
        <Container className="flex h-[72px] items-center justify-between">
        <Logo />

        <nav
          aria-label="Primary"
          className="hidden items-center gap-1 lg:flex"
        >
          {mainNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "rounded-full px-4 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <CtaButton
            to={siteConfig.primaryCta.path}
            withArrow
            magnetic
            data-testid="header-cta"
          >
            {siteConfig.primaryCta.label}
          </CtaButton>
        </div>

        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border text-foreground lg:hidden"
          aria-label="Open menu"
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </Container>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="overflow-hidden border-t border-border bg-background lg:hidden"
            data-testid="mobile-nav"
          >
            <Container className="flex flex-col gap-1 py-6">
              {mainNav.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center justify-between rounded-xl px-4 py-3 text-base font-medium",
                      isActive
                        ? "bg-primary/5 text-primary"
                        : "text-foreground hover:bg-foreground/[0.03]",
                    )
                  }
                >
                  {item.label}
                  <span className="text-xs text-muted-foreground">
                    {item.description}
                  </span>
                </NavLink>
              ))}
              <div className="mt-3">
                <CtaButton
                  to={siteConfig.primaryCta.path}
                  withArrow
                  className="w-full"
                >
                  {siteConfig.primaryCta.label}
                </CtaButton>
              </div>
            </Container>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </header>
  );
}
